import { CACHE_CONFIG } from '@lib/config/cache';

/**
 * ImageCacheService
 *
 * Manages in-memory caching of blob URLs for authenticated images.
 * Works in conjunction with Service Worker caching to provide multi-layer caching:
 * 1. In-memory blob URL cache (fastest, for current session)
 * 2. Service Worker cache (persistent, survives page reloads)
 *
 * This prevents:
 * - Redundant fetch requests for the same image
 * - Recreating blob URLs for already-fetched images
 * - Memory leaks by properly managing blob URL lifecycle
 */

interface CacheEntry {
  blobUrl: string;
  originalUrl: string;
  timestamp: number;
  size: number;
  refCount: number;
}

class ImageCacheService {
  private cache: Map<string, CacheEntry> = new Map();
  private maxCacheSize = CACHE_CONFIG.memory.maxSize;
  private currentCacheSize = 0;
  private cleanupInterval: number | null = null;
  private readonly CACHE_TTL = CACHE_CONFIG.memory.ttl;
  private readonly CLEANUP_INTERVAL = CACHE_CONFIG.memory.cleanupInterval;

  constructor() {
    this.startCleanupTimer();

    // Listen for Service Worker updates
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'CACHE_CLEARED') {
          this.clearMemoryCache();
        }
      });
    }
  }

  /**
   * Synchronously return a cached blob URL if one exists and is still valid.
   * Does NOT fetch — returns null on cache miss.
   */
  getCachedImage(url: string, token: string): string | null {
    const cacheKey = this.getCacheKey(url, token);
    const cached = this.cache.get(cacheKey);
    if (cached && this.isCacheEntryValid(cached)) {
      cached.refCount++;
      cached.timestamp = Date.now();
      return cached.blobUrl;
    }
    return null;
  }

  /**
   * Peek at a cached blob URL without incrementing refCount.
   * Use this for synchronous initialisation (e.g. useState) where the
   * proper acquire/release lifecycle is handled elsewhere (e.g. useEffect).
   */
  peekCachedImage(url: string, token: string): string | null {
    const cacheKey = this.getCacheKey(url, token);
    const cached = this.cache.get(cacheKey);
    if (cached && this.isCacheEntryValid(cached)) {
      return cached.blobUrl;
    }
    return null;
  }

  /**
   * Get a cached image blob URL or fetch and cache it
   */
  async getImage(url: string, token: string, signal?: AbortSignal): Promise<string> {
    const cacheKey = this.getCacheKey(url, token);
    const cached = this.cache.get(cacheKey);

    // Return cached blob URL if it exists and is still valid
    if (cached && this.isCacheEntryValid(cached)) {
      cached.refCount++;
      cached.timestamp = Date.now(); // Update timestamp for LRU
      return cached.blobUrl;
    }

    // If we had an old entry, clean it up
    if (cached) {
      this.revokeBlobUrl(cacheKey);
    }

    // Fetch the image
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
      signal,
    });

    if (!response.ok) {
      throw new Error(`Failed to load image: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);

    // Cache the blob URL
    const entry: CacheEntry = {
      blobUrl,
      originalUrl: url,
      timestamp: Date.now(),
      size: blob.size,
      refCount: 1,
    };

    this.cache.set(cacheKey, entry);
    this.currentCacheSize += blob.size;

    // Evict old entries if cache is too large
    this.evictIfNeeded();

    return blobUrl;
  }

  /**
   * Release a reference to a cached image
   * When refCount reaches 0, the entry becomes eligible for cleanup
   */
  releaseImage(url: string, token: string): void {
    const cacheKey = this.getCacheKey(url, token);
    const cached = this.cache.get(cacheKey);

    if (cached) {
      cached.refCount = Math.max(0, cached.refCount - 1);
    }
  }

  /**
   * Preload images into cache (useful for prefetching thumbnails)
   */
  async preloadImages(images: Array<{ url: string; token: string }>): Promise<void> {
    const promises = images.map(({ url, token }) =>
      this.getImage(url, token).catch((error) => {
        console.warn(`Failed to preload image ${url}:`, error);
      }),
    );

    await Promise.allSettled(promises);
  }

  /**
   * Clear all cached blob URLs
   */
  clearMemoryCache(): void {
    for (const [key] of this.cache) {
      this.revokeBlobUrl(key);
    }
    this.cache.clear();
    this.currentCacheSize = 0;
  }

  /**
   * Get cache statistics
   */
  getStats() {
    return {
      entries: this.cache.size,
      sizeInBytes: this.currentCacheSize,
      sizeInMB: (this.currentCacheSize / 1024 / 1024).toFixed(2),
      maxSizeInMB: (this.maxCacheSize / 1024 / 1024).toFixed(2),
    };
  }

  private getCacheKey(url: string, token: string): string {
    // Include a hash of the token to ensure different users/sessions don't share cached images
    // But we don't need the full token for security reasons
    const tokenHash = this.simpleHash(token).toString(36);
    return `${url}::${tokenHash}`;
  }

  private simpleHash(str: string): number {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
  }

  private isCacheEntryValid(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < this.CACHE_TTL;
  }

  private revokeBlobUrl(cacheKey: string): void {
    const entry = this.cache.get(cacheKey);
    if (entry) {
      URL.revokeObjectURL(entry.blobUrl);
      this.currentCacheSize -= entry.size;
      this.cache.delete(cacheKey);
    }
  }

  private evictIfNeeded(): void {
    if (this.currentCacheSize <= this.maxCacheSize) {
      return;
    }

    // Sort entries by timestamp (LRU) and refCount (prioritize unused)
    const entries = Array.from(this.cache.entries()).sort(([, a], [, b]) => {
      // First, prioritize entries with 0 references
      if (a.refCount === 0 && b.refCount > 0) return -1;
      if (b.refCount === 0 && a.refCount > 0) return 1;
      // Then sort by timestamp (oldest first)
      return a.timestamp - b.timestamp;
    });

    // Evict entries until we're under the limit
    const targetSize = this.maxCacheSize * CACHE_CONFIG.memory.evictionTarget;
    let evictedCount = 0;
    for (const [key, entry] of entries) {
      if (this.currentCacheSize <= targetSize) {
        break; // Evict to target capacity to avoid constant eviction
      }
      this.revokeBlobUrl(key);
      evictedCount++;
    }

    if (evictedCount > 0) {
      console.log(`[ImageCache] Evicted ${evictedCount} entries to free memory`);
    }
  }

  private startCleanupTimer(): void {
    if (typeof window === 'undefined') return;

    this.cleanupInterval = window.setInterval(() => {
      this.cleanup();
    }, this.CLEANUP_INTERVAL);

    // Clean up on page unload
    window.addEventListener('beforeunload', () => {
      this.clearMemoryCache();
      if (this.cleanupInterval) {
        clearInterval(this.cleanupInterval);
      }
    });
  }

  private cleanup(): void {
    const now = Date.now();
    let cleanedCount = 0;

    for (const [key, entry] of this.cache.entries()) {
      // Remove entries that are expired and not referenced
      if (entry.refCount === 0 && now - entry.timestamp > this.CACHE_TTL) {
        this.revokeBlobUrl(key);
        cleanedCount++;
      }
    }

    if (cleanedCount > 0) {
      console.log(`[ImageCache] Cleaned up ${cleanedCount} expired entries`);
    }
  }
}

// Export singleton instance
export const imageCacheService = new ImageCacheService();
